// src/components/registration/steps/StepCompanySetup.jsx
import React, { useEffect } from "react";
import InputFormAI from "../../systemUtils/InputFormAI";
import LoadingCircle from "../../../utils/LoadingCircle";
import ResultScreen from "../../systemUtils/ResultScreen";
import { useDispatch, useSelector } from "react-redux";
import {
  setRegistrationData,
  setRegistrationStep,
} from "../../../../redux/actionsFolder/registrationActions";
import { useNavigate } from "react-router-dom";

const StepCompanySetup = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const registrationData = useSelector(
    (state) => state.registration.registrationData
  );

  // Ensure previous step (StepWelcome) is complete
  useEffect(() => {
    if (!registrationData.userEmail || !registrationData.company) {
      navigate("/register-hero/welcome");
    }
  }, [registrationData, navigate]);

  // Definitions (copied from your existing code)
  const uniqueId = "companyInformation";
  const dataPointDefinitions = {
    name: "The name of the company or the organisation.",
    RegistrationNumber: "The company registration number.",
    companyDirectors:
      "If a single director, then the director's name and surname. If multiple directors are registered, then a list of director names. List must be formatted in a Javascript list",
    companyDirectorsID:
      "If single director, then the director's ID number. If multiple directors, then a list of director ID numbers. List must be formatted in a Javascript list",
    productService: "The product or service provided by the company.",
    contactPerson: "The main contact person at the company.",
    contactNumber: "The phone number of the contact person.",
    contactEmail: "The email address of the contact person",
    address: "The full physical address of the company.",
    bank: "The name of the bank where the company's account is held.",
    bankAccountNumber: "The company's bank account number.",
    accountType:
      "The type of bank account, such as savings, current, check, or business.",
    branchCode:
      "The branch code of the bank where the company holds an account.",
  };

  const duplicates = {
    name: "prohibited",
    RegistrationNumber: "prohibited",
    contactPerson: "accepted",
    contactNumber: "accepted",
    companyDirectors: "accepted",
    companyDirectorsID: "accepted",
    productService: "accepted",
    bank: "accepted",
    bankAccountNumber: "prohibited",
    accountType: "accepted",
    branchCode: "accepted",
    defaultPaymentRef: "accepted",
    contactEmail: "accepted",
  };

  const sections = [
    {
      title: "Document Upload",
      isDocumentUpload: true,
      fields: [
        {
          name: "companyRegistration",
          label: "Company Registration Document",
          type: "file",
          accept: "application/pdf",
          required: true,
          description:
            "A valid company registration document containing a registration number and the legally registered company name.",
        },
        {
          name: "proofOfBankAccount",
          label: "Proof of Bank Account",
          type: "file",
          accept: "application/pdf",
          required: false,
          description:
            "A proof of bank account is required to verify ownership and ensure accurate future transactions. This bank account details will be used for invoicing so the accuracy of the account number is important",
        },
        {
          name: "taxCertificate",
          label: "Tax Certificate",
          type: "file",
          accept: "application/pdf",
          required: false,
          description:
            "Tax clearance certificate that can provide details like the registered company name, address and tax numbers",
        },
        {
          name: "directorsIDDocument",
          label:
            "Directors' ID Documents. Upload Individual ID or Multiple Scans in Single Document",
          type: "file",
          accept: "application/pdf",
          required: false,
          description:
            "ID documents for each director of the company are required for verification purposes. A company can have multiple directors. The attached file can contain copies of multiple director IDs or just a single copy of an ID",
        },
      ],
    },
    {
      title: "General Information",
      isDocumentUpload: false,
      fields: [
        { name: "name", label: "Company Name", type: "text", required: true },
        {
          name: "RegistrationNumber",
          label: "Company Registration Number",
          type: "text",
          required: true,
        },
        {
          name: "companyDirectors",
          label: "Names of Company Directors",
          type: "text",
          required: true,
        },
        {
          name: "companyDirectorsID",
          label: "Company Directors' ID Numbers",
          type: "text",
          required: true,
        },
        {
          name: "productService",
          label: "Product/Service",
          type: "text",
          required: true,
        },
        {
          name: "contactPerson",
          label: "Contact Person",
          type: "text",
          required: true,
        },
        {
          name: "contactNumber",
          label: "Contact Number",
          type: "text",
          required: true,
        },
        {
          name: "contactEmail",
          label: "Email",
          type: "email",
          required: true,
        },
        {
          name: "address",
          label: "Address",
          type: "text",
          required: true,
        },
      ],
    },
    {
      title: "Bank Information",
      isDocumentUpload: false,
      fields: [
        {
          name: "bank",
          label: "Bank",
          type: "dropdown",
          options: [
            "First National Bank",
            "Nedbank",
            "Standard Bank",
            "ABSA",
            "Discovery Bank",
            "Capitec",
            "Other",
          ],
          required: true,
        },
        {
          name: "bankAccountNumber",
          label: "Bank Account Number",
          type: "text",
          required: true,
        },
        {
          name: "accountType",
          label: "Account Type",
          type: "text",
          required: true,
        },
        {
          name: "branchCode",
          label: "Branch Code",
          type: "text",
          required: false,
        },
        {
          name: "defaultPaymentRef",
          label: "Default Payment Ref",
          type: "text",
          required: false,
        },
      ],
    },
  ];

  const validationRules = {
    bankAccountNumber: {
      validate: (value) => /^[0-9]+$/.test(value),
      errorMessage: "Bank account number must be a valid number.",
    },
    contactEmail: {
      validate: (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
      errorMessage: "Please enter a valid email address.",
    },
    contactNumber: {
      validate: (value) => /^[0-9]+$/.test(value),
      errorMessage: "Contact number must contain only numbers.",
    },
    bank: {
      validate: (value) => value !== "",
      errorMessage: "Please select a bank.",
    },
  };

  // Handler when the form completes
  const handleComplete = (formData) => {
    // Merge the new form data with existing registration data
    dispatch(setRegistrationData(formData));
    // Update the registration step and navigate to the next step
    dispatch(setRegistrationStep("add-user-groups"));
    navigate("/register-hero/add-user-groups");
  };

  return (
    <div className="add-user-container">
      <h2 className="add-user-heading">Company Setup</h2>
      {false && <LoadingCircle label="Processing..." />}
      {false && (
        <ResultScreen
          type="success"
          message="Company setup completed successfully."
          onContinue={handleComplete}
        />
      )}
      <InputFormAI
        sections={sections}
        initialFormData={{}}
        company={registrationData.company}
        collectionBrand={registrationData.collectionBrand}
        systemName={registrationData.systemName}
        uniqueId={uniqueId}
        isNew={true}
        checkDuplicateFields={[]}
        validationRules={validationRules}
        dataPointDefinitions={dataPointDefinitions}
        userEmail={registrationData.userEmail}
        approvalGate={{ enabled: false }}
        onComplete={handleComplete}
        duplicates={duplicates}
      />
    </div>
  );
};

export default StepCompanySetup;
